import { OverwatchUnit } from './types';

export interface IOverwatchCardWidget {
  /** Contract widget key, as discovery lists it. */
  key: string;
  /** The data key whose state decides the whole card. */
  primary: string;
  /** Contract units, used when discovery omits a key's unit. */
  units: Record<string, OverwatchUnit>;
  /** A contract proposal not yet approved (System & Support). */
  proposed?: boolean;
}

const widget = (
  key: string,
  primary: string,
  units: Record<string, OverwatchUnit>,
  proposed = false
): IOverwatchCardWidget => ({ key, primary, units, ...(proposed ? { proposed } : {}) });

const proposal = (key: string, primary: string, units: Record<string, OverwatchUnit> = {}) =>
  widget(key, primary, units, true);

/** Cards rendered from the widget API, keyed by the card they feed. */
export const OW_CARD_WIDGETS = {
  engagementRate: widget('campaign_performance.engagement_rate', 'engagement_rate', {
    engagement_rate: 'percent',
    trend: 'percent'
  }),
  campaignCacSavings: widget('campaign_performance.cac_savings', 'cac_savings', {
    cac_savings: 'currency',
    cac_savings_change: 'percent_change',
    trend: 'currency'
  }),
  activeCampaigns: widget('campaign_performance.active_campaigns', 'active_campaigns', {
    active_campaigns: 'count',
    net_new: 'count',
    trend: 'count'
  }),
  completionRate: widget('campaign_performance.completion_rate', 'completion_rate', {
    completion_rate: 'percent',
    trend: 'percent'
  }),
  progressionRate: widget('campaign_performance.progression_rate', 'progression_rate', {
    progression_rate: 'percent',
    trend: 'percent'
  }),
  issuedOutcomeRate: widget('campaign_performance.issued_outcome_rate', 'issued_outcome_rate', {
    issued_outcome_rate: 'percent',
    trend: 'percent'
  }),
  executionStatus: widget('execution_status.execution_status', 'running', {
    running: 'count',
    completed: 'count',
    failed: 'count',
    scheduled: 'count',
    warnings: 'count'
  }),
  totalApiCalls: widget('operational_visibility.total_api_calls', 'total_api_calls', {
    total_api_calls: 'count',
    total_api_calls_change: 'percent_change',
    trend: 'count'
  }),
  totalEventIngestion: widget(
    'operational_visibility.total_event_ingestion',
    'total_event_ingestion',
    {
      total_event_ingestion: 'count',
      total_event_ingestion_change: 'percent_change',
      trend: 'count'
    }
  ),
  platformUptime: widget('operational_visibility.platform_uptime', 'platform_uptime', {
    platform_uptime: 'percent',
    uptime_target: 'percent'
  }),
  activeIncidents: widget('operational_visibility.active_incidents', 'p1_count', {
    p1_count: 'count',
    p2_count: 'count',
    p3_count: 'count'
  }),
  roi: widget('executive_commercial_intelligence.roi_and_commercial_value', 'roi', {
    roi: 'ratio',
    roi_change: 'ratio',
    net_incremental_value: 'currency'
  }),
  commercialCacSavings: widget('executive_commercial_intelligence.cac_savings', 'cac_savings', {
    cac_savings: 'currency',
    cac_savings_change: 'percent_change',
    trend: 'currency'
  }),
  topLineGenerated: widget(
    'executive_commercial_intelligence.top_line_generated',
    'top_line_generated',
    { top_line_generated: 'currency', trend: 'currency' }
  ),
  netProfitability: widget(
    'executive_commercial_intelligence.net_profitability',
    'net_profitability',
    { net_profitability: 'currency', trend: 'currency' }
  ),
  transactionsProcessed: widget(
    'executive_commercial_intelligence.transactions_processed',
    'transactions_processed',
    { transactions_processed: 'count', trend: 'count' }
  ),
  clusterHealth: widget('system_health_overview.overall_cluster_health', 'cluster_health', {
    cluster_health: 'percent'
  }),
  slaCompliance: widget('system_health_overview.sla_compliance_rate', 'sla_compliance_rate', {
    sla_compliance_rate: 'percent',
    sla_compliance_target: 'percent'
  }),
  activeSlaPlan: widget('system_health_overview.active_sla_plan', 'plan_name', {}),
  rewardsFulfillment: widget('engagement_summary.rewards_fulfillment', 'redemption_rate', {
    redemption_rate: 'percent',
    issued_rewards: 'count',
    redeemed_rewards: 'count',
    issued_trend: 'count',
    redeemed_trend: 'count'
  }),
  loyaltyPoints: widget('engagement_summary.loyalty_points_currency', 'points_earned', {
    points_earned: 'count',
    points_earned_change: 'percent_change',
    points_burned: 'count',
    net_points: 'count',
    earn_burn_ratio: 'ratio',
    trend: 'count'
  }),
  mauTruConversion: widget('engagement_summary.mau_to_tru_conversion', 'conversion_rate', {
    tru_count: 'count',
    tru_count_12m_average: 'count',
    mau_count: 'count',
    mau_count_12m_average: 'count',
    conversion_rate: 'percent',
    conversion_rate_12m_average: 'percent'
  }),
  topPerformingCampaigns: widget(
    'campaign_attention.top_performing_campaigns',
    'funnel_completion_rate',
    {}
  ),
  needsAttention: widget('campaign_attention.needs_attention', 'funnel_completion_rate', {}),
  workloadShare: widget('execution_status.workload_share', 'total_executions', {
    total_executions: 'count',
    campaign_execution_share: 'percent',
    reward_processing_share: 'percent',
    notification_engine_share: 'percent',
    event_ingestion_share: 'percent',
    scheduled_tasks_share: 'percent'
  }),
  executionStream: widget('execution_status.realtime_execution_engine_stream', 'jobs', {}),
  campaignPerformanceTrends: widget(
    'performance_and_user_growth.campaign_performance_trends',
    'completion',
    { completion: 'percent', progression: 'percent' }
  ),
  userGrowthTrends: widget('performance_and_user_growth.user_growth_trends', 'mau', {
    mau: 'count',
    tru: 'count'
  }),
  detectedAnomalies: widget(
    'detected_anomalies_and_required_actions.detected_anomalies',
    'anomalies',
    {}
  ),
  hourlyIngestion: widget('operational_visibility.hourly_ingestion_throughput', 'throughput', {
    throughput: 'count',
    total_throughput: 'count'
  }),
  apiEconomics: widget('operational_visibility.account_api_economics', 'acv_per_1k_api_calls', {
    acv_per_1k_api_calls: 'currency',
    acv_per_peak_mau: 'currency'
  }),
  quarterlyCostVsValue: widget(
    'executive_commercial_intelligence.quarterly_cost_vs_value',
    'value_created',
    { value_created: 'currency', platform_cost: 'currency' }
  ),
  contractDetails: widget(
    'contract_summary_and_commercial_terms.contract_details',
    'contract_title',
    {
      days_to_renewal: 'count',
      annual_contract_value: 'currency',
      total_contract_value: 'currency'
    }
  ),
  subscribedCapabilities: widget(
    'your_perx_capabilities.subscribed_capabilities',
    'capabilities',
    {}
  ),
  discoverableCapabilities: widget(
    'explore_more_with_perx.discoverable_capabilities',
    'capabilities',
    {}
  ),
  news: widget('whats_new_in_perx.news', 'releases', {}),
  weeklyUpdate: widget('weekly_updates.weekly_update', 'update_title', {}),
  weeklyUpdateHistory: widget('weekly_updates.weekly_update_history', 'updates', {}),
  activePartnerState: proposal('system_health_overview.active_partner_state', 'partners'),
  slaPlan: proposal('sla_plan_details.sla_plan', 'support_plan_name', {
    uptime_commitment: 'percent'
  }),
  incidentBoard: proposal('incident_and_maintenance_board.board', 'incidents'),
  supportTickets: proposal('active_support_tickets.support_tickets', 'tickets'),
  knowledgeArticles: proposal('enterprise_knowledge_base_and_playbooks.articles', 'articles'),
  securityDocuments: proposal('security_and_audit_compliance_documents.documents', 'documents'),
  platformComponents: proposal(
    'approved_platform_technology_and_engine_versions.components',
    'components'
  ),
  auditEvents: proposal('audit_and_activity_log.events', 'events')
} as const;

export type OverwatchCardName = keyof typeof OW_CARD_WIDGETS;

/** Per-section widget keys; each tab sends its sections' keys in one batch, as the contract asks. */
const W = OW_CARD_WIDGETS;

/** Per-section widget keys; each tab sends its sections' keys in one batch, as the contract asks. */
export const OW_SECTION_WIDGETS = {
  campaignPerformance: [
    W.engagementRate,
    W.campaignCacSavings,
    W.activeCampaigns,
    W.completionRate,
    W.progressionRate,
    W.issuedOutcomeRate
  ],
  engagementSummary: [W.rewardsFulfillment, W.loyaltyPoints, W.mauTruConversion],
  campaignAttention: [W.topPerformingCampaigns, W.needsAttention],
  executionStatus: [W.executionStatus, W.workloadShare, W.executionStream],
  performanceGrowth: [W.campaignPerformanceTrends, W.userGrowthTrends],
  anomalies: [W.detectedAnomalies],
  operationalVisibility: [
    W.totalApiCalls,
    W.totalEventIngestion,
    W.platformUptime,
    W.activeIncidents,
    W.hourlyIngestion,
    W.apiEconomics
  ],
  commercialIntelligence: [
    W.roi,
    W.commercialCacSavings,
    W.topLineGenerated,
    W.netProfitability,
    W.transactionsProcessed,
    W.quarterlyCostVsValue
  ],
  contractSummary: [W.contractDetails],
  capabilities: [W.subscribedCapabilities],
  exploreMore: [W.discoverableCapabilities],
  whatsNew: [W.news],
  weeklyUpdates: [W.weeklyUpdate],
  systemHealth: [W.clusterHealth, W.slaCompliance, W.activePartnerState, W.activeSlaPlan],
  slaIncidents: [W.slaPlan, W.incidentBoard],
  activeTickets: [W.supportTickets],
  knowledgeSecurity: [W.knowledgeArticles, W.securityDocuments],
  platformVersions: [W.platformComponents],
  auditLog: [W.auditEvents]
};

export const widgetKeysOf = (widgets: IOverwatchCardWidget[]): string[] =>
  widgets.map(item => item.key);
